import { z } from 'zod'

import { PERMISSION, PERMISSIONS } from './permissions'
import { ROUTE_PERMISSIONS } from './route-permissions'

/**
 * Ordered list of every known permission, used to encode permissions as
 * small integer indices instead of full strings when persisting them
 * (e.g. in the session cookie), to keep the cookie size small.
 */
const PERMISSION_LIST = Object.values(PERMISSIONS) as PERMISSION[]

const PERMISSION_INDEX_MAP = new Map<PERMISSION, number>(
  PERMISSION_LIST.map((permission, index) => [permission, index])
)

/**
 * Decodes an array of indices back into their permission strings.
 */
export function decodePermissions(
  indices: null | number[] | undefined
): null | PERMISSION[] {
  if (!indices || indices.length === 0) return null

  const permissions: PERMISSION[] = []
  for (const index of indices) {
    const permission = PERMISSION_LIST[index]
    if (permission !== undefined) permissions.push(permission)
  }

  return permissions
}

/**
 * Encodes an array of permission strings into their indices in PERMISSIONS.
 * Unknown permissions (not defined in PERMISSIONS) are dropped.
 */
export function encodePermissions(
  permissions: null | PERMISSION[] | readonly PERMISSION[] | undefined
): null | number[] {
  if (!permissions || permissions.length === 0) return null

  const indices: number[] = []
  for (const permission of permissions) {
    const index = PERMISSION_INDEX_MAP.get(permission)
    if (index !== undefined) indices.push(index)
  }

  return indices
}

/**
 * Zod schema for validating permission strings.
 * Ensures that only permissions defined in PERMISSIONS are valid.
 */
export const PermissionSchema = z.enum(
  Object.values(PERMISSIONS) as [PERMISSION, ...PERMISSION[]]
)

/**
 * Extracts user permissions from a role object.
 * If permission_type is 'all', returns [SUPER_ADMIN].
 * Otherwise, returns the role's permissions array (or empty array if null/undefined).
 *
 * @param role - Role object with permission_type and permissions
 * @returns Array of PERMISSION values
 */
export function extractUserPermissions(role: {
  permission_type: 'all' | 'custom'
  permissions: null | PERMISSION[] | readonly PERMISSION[] | undefined
}): PERMISSION[] {
  if (role.permission_type === 'all') {
    return [PERMISSIONS.SUPER_ADMIN]
  }
  // Convert readonly array to mutable array if needed
  return role.permissions ? [...role.permissions] : []
}

/**
 * Gets the required permission for a given route pathname.
 * Handles exact matches, dynamic routes, and query string patterns.
 *
 * @param pathname - The route pathname (may include query string)
 * @returns The required permission for the route, or null if no permission is required
 */
export function getRequiredPermissionForRoute(
  pathname: string
): null | PERMISSION {
  // Extract pathname and query string
  const [path, query] = pathname.split('?')
  const queryParams = new URLSearchParams(query || '')

  // Exact match first (with query string)
  if (ROUTE_PERMISSIONS[pathname]) {
    return ROUTE_PERMISSIONS[pathname]
  }

  // Try to match with query string pattern if tab parameter exists
  if (queryParams.has('tab')) {
    const tabValue = queryParams.get('tab')
    const pathWithTab = `${path}?tab=${tabValue}`
    if (ROUTE_PERMISSIONS[pathWithTab]) {
      return ROUTE_PERMISSIONS[pathWithTab]
    }
  }

  // Exact match for path without query string
  if (ROUTE_PERMISSIONS[path]) {
    return ROUTE_PERMISSIONS[path]
  }

  // Try to match dynamic routes by replacing dynamic segments
  for (const [routePattern, permission] of Object.entries(ROUTE_PERMISSIONS)) {
    // Skip query string patterns for now (we'll handle them separately)
    if (routePattern.includes('?')) {
      continue
    }

    // Convert route pattern to regex
    const pattern = routePattern
      .replace(/\[([^\]]+)\]/g, '[^/]+') // Replace [id] with [^/]+
      .replace(/\[\[\.\.\.([^\]]+)\]\]/g, '.*') // Replace [[...slug]] with .*
      .replace(/\[\[([^\]]+)\]\]/g, '[^/]+') // Replace [[slug]] with [^/]+

    const regex = new RegExp(`^${pattern}$`)
    if (regex.test(path)) {
      console.log('routePattern', routePattern, 'permission', permission)
      return permission
    }
  }

  // Try matching with query string patterns for dynamic routes
  if (queryParams.has('tab')) {
    const tabValue = queryParams.get('tab')
    for (const [routePattern, permission] of Object.entries(
      ROUTE_PERMISSIONS
    )) {
      if (!routePattern.includes('?')) {
        continue
      }

      // Extract path and query from pattern
      const [patternPath, patternQuery] = routePattern.split('?')
      const patternParams = new URLSearchParams(patternQuery || '')
      const patternTab = patternParams.get('tab')

      // Check if tab matches
      if (patternTab !== tabValue) {
        continue
      }

      // Convert route pattern to regex
      const pattern = patternPath
        .replace(/\[([^\]]+)\]/g, '[^/]+') // Replace [id] with [^/]+
        .replace(/\[\[\.\.\.([^\]]+)\]\]/g, '.*') // Replace [[...slug]] with .*
        .replace(/\[\[([^\]]+)\]\]/g, '[^/]+') // Replace [[slug]] with [^/]+

      const regex = new RegExp(`^${pattern}$`)
      if (regex.test(path)) {
        console.log('routePattern', routePattern, 'permission', permission)
        return permission
      }
    }
  }

  // No specific permission required for this route
  return null
}

/**
 * Checks if a user has a required permission.
 * Returns true if the user has the direct permission or has SUPER_ADMIN permission.
 *
 * @param userPermissions - Array of user's permissions (can be null/undefined/empty)
 * @param requiredPermission - The permission to check for
 * @returns true if user has the permission, false otherwise
 */
export function hasPermission(
  userPermissions: null | PERMISSION[] | readonly PERMISSION[] | undefined,
  requiredPermission: PERMISSION
): boolean {
  if (!userPermissions || userPermissions.length === 0) return false

  const hasDirectPermission = userPermissions.includes(requiredPermission)
  const hasSuperAdmin = userPermissions.includes(PERMISSIONS.SUPER_ADMIN)

  return hasDirectPermission || hasSuperAdmin
}

/**
 * Determines if a route is protected and what permission is required.
 *
 * @param pathname - The route pathname
 * @returns Object with isProtected flag and requiredPermission
 */
export function isProtectedRoute(pathname: string): {
  isProtected: boolean
  requiredPermission: null | PERMISSION
} {
  // Auth pages don't need permission checking
  if (
    pathname.includes('/login') ||
    pathname.includes('/forgot-password') ||
    pathname.includes('/unauthorized')
  ) {
    return {
      isProtected: false,
      requiredPermission: null
    }
  }

  // Root dashboard page doesn't need permission checking (default access)
  if (pathname === '/') {
    return {
      isProtected: false,
      requiredPermission: null
    }
  }

  // Check if pathname matches any protected route
  const requiredPermission = getRequiredPermissionForRoute(pathname)
  const isProtected = requiredPermission !== null
  return {
    isProtected,
    requiredPermission
  }
}

/**
 * Validates an array of permissions against the PermissionSchema.
 * Logs errors for any invalid permissions that don't exist in PERMISSIONS.
 * @param permissions - Array of permission strings to validate (can be null/undefined)
 * @returns The original permissions array (validated but not transformed)
 */
export function validatePermissions(
  permissions: null | PERMISSION[] | readonly PERMISSION[] | undefined
): null | PERMISSION[] | readonly PERMISSION[] | undefined {
  if (!permissions || permissions.length === 0) return permissions

  const invalidPermissions: string[] = []

  for (const permission of permissions) {
    const result = PermissionSchema.safeParse(permission)
    if (!result.success) {
      invalidPermissions.push(permission)
    }
  }

  if (invalidPermissions.length > 0) {
    console.error(
      '%c[PERMISSION VALIDATION ERROR]%c Invalid permissions received from server that are not defined in PERMISSIONS:',
      'color:#ef4444;font-weight:bold',
      'color:inherit',
      invalidPermissions
    )
    console.error(
      'These permissions should be added to lib/auth/permissions.ts or removed from the server response.'
    )
  }

  return permissions
}
